/**
 * Account-key wrapping crypto. The per-user X25519 account private key is
 * stored on the server wrapped under an HKDF(exportKey, "account-key-wrapping")
 * key, and locally wrapped under the PBKDF2-derived local wrapping key for
 * offline unlock. Shared by the web app and the browser extension.
 */

import { x25519 } from '@noble/curves/ed25519.js';

const ACCOUNT_HKDF_INFO = 'account-key-wrapping';
const IV_LENGTH = 12;

function bytesToBase64(b: Uint8Array): string {
  let s = '';
  for (const c of b) s += String.fromCharCode(c);
  return btoa(s);
}

function base64ToBytes(b64: string): Uint8Array {
  return Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
}

function base64UrlToBytes(b64url: string): Uint8Array {
  const norm = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const pad = norm.length % 4 === 0 ? '' : '='.repeat(4 - (norm.length % 4));
  return base64ToBytes(norm + pad);
}

/**
 * Generate a per-user X25519 account keypair. Returns the raw 32-byte public
 * and private keys (decoded from the WebCrypto JWK `x`/`d` fields). Every user
 * has exactly one, minted once at registration and stored twice-wrapped (server
 * wrap under `deriveAccountWrappingKey`, local wrap under the PBKDF2 key). Web
 * and the extension must produce byte-identical keys, so this lives in shared.
 */
export async function generateAccountKeyPair(): Promise<{
  publicKey: Uint8Array;
  privateKey: Uint8Array;
}> {
  const keyPair = (await crypto.subtle.generateKey({ name: 'X25519' }, true, ['deriveBits'])) as CryptoKeyPair;
  const publicJwk = await crypto.subtle.exportKey('jwk', keyPair.publicKey);
  const privateJwk = await crypto.subtle.exportKey('jwk', keyPair.privateKey);
  // JWK 'x' is the public key, 'd' the private key (base64url, unpadded).
  return {
    publicKey: base64UrlToBytes(publicJwk.x!),
    privateKey: base64UrlToBytes(privateJwk.d!),
  };
}

/**
 * Derive the raw 32-byte X25519 account PUBLIC key from the raw private key.
 *
 * Security (PKA finding H1): a client must never trust the server's copy of its
 * *own* account public key — a malicious server could serve an attacker-held
 * key, and a self-wrap (e.g. the family-key handoff at family creation) would
 * then hand the family private key to the server. The private key is authentic
 * on every unlock path (unwrapped under a key the server never sees), so the
 * public half is recomputed from it here instead of being read back from the
 * server. X25519 scalar-mult is deterministic, so the output is byte-identical
 * to the public key `generateAccountKeyPair` (WebCrypto) produced at
 * registration — asserted by the parity golden vector in the test suite. The
 * hand-ported Kotlin/Swift clients mirror the same frozen vector.
 */
export function deriveAccountPublicKey(privateKey: Uint8Array): Uint8Array {
  return x25519.getPublicKey(privateKey);
}

/**
 * Server-side wrap of the account private key under the account wrapping key
 * (HKDF(exportKey, "account-key-wrapping")). AES-256-GCM with a fresh 12-byte
 * IV — the same envelope as `localWrapAccountPrivateKey`, differing only in the
 * wrapping key. Output feeds `PUT /users/me/account-key`.
 */
export async function wrapAccountPrivateKey(
  privateKey: Uint8Array,
  wrappingKey: CryptoKey,
): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, wrappingKey, privateKey as BufferSource),
  );
  return { ciphertext: bytesToBase64(ciphertext), iv: bytesToBase64(iv) };
}

export async function deriveAccountWrappingKey(exportKey: string): Promise<CryptoKey> {
  const normalized = exportKey.replace(/-/g, '+').replace(/_/g, '/');
  const keyBytes = base64ToBytes(normalized);

  const keyMaterial = await crypto.subtle.importKey('raw', keyBytes as BufferSource, 'HKDF', false, ['deriveKey']);

  return crypto.subtle.deriveKey(
    {
      name: 'HKDF',
      hash: 'SHA-256',
      salt: new Uint8Array(32),
      info: new TextEncoder().encode(ACCOUNT_HKDF_INFO),
    },
    keyMaterial,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

export async function unwrapAccountPrivateKey(
  ciphertext: string,
  iv: string,
  wrappingKey: CryptoKey,
): Promise<Uint8Array> {
  const ctBytes = base64ToBytes(ciphertext);
  const ivBytes = base64ToBytes(iv);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      wrappingKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
}

export async function localWrapAccountPrivateKey(
  privateKey: Uint8Array,
  localWrappingKey: CryptoKey,
): Promise<{ ciphertext: string; iv: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH) as Uint8Array<ArrayBuffer>);
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, localWrappingKey, privateKey as BufferSource),
  );
  return { ciphertext: bytesToBase64(ciphertext), iv: bytesToBase64(iv) };
}

export async function localUnwrapAccountPrivateKey(
  ciphertext: string,
  iv: string,
  localWrappingKey: CryptoKey,
): Promise<Uint8Array> {
  const ctBytes = base64ToBytes(ciphertext);
  const ivBytes = base64ToBytes(iv);
  return new Uint8Array(
    await crypto.subtle.decrypt(
      { name: 'AES-GCM', iv: ivBytes.buffer as ArrayBuffer },
      localWrappingKey,
      ctBytes.buffer as ArrayBuffer,
    ),
  );
}
